import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, MapPin, Calendar, Grid, List, Map, X, SlidersHorizontal, Bike, Footprints, Medal, Mountain, Dumbbell, Swords, Car, Wind, Flag, CircleDot, Target, ChevronRight, Sparkles, PersonStanding, Gauge, Zap, TreePine, Aperture, Diamond, Gem, Flame } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../utils/dateLocale';
import EventCard from '../components/EventCard';
import SponsorStripBanner from '../components/SponsorStripBanner';
import { eventsApi, categoriesApi } from '../services/api';
import showcaseEvents from '../data/showcaseEvents';

const EventsMap = React.lazy(() => import('../components/EventsMap'));

const TennisRacquet = ({ className, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <circle cx="12" cy="9" r="7" />
    <line x1="12" y1="16" x2="12" y2="23" />
    <line x1="9" y1="5" x2="9" y2="13" /><line x1="15" y1="5" x2="15" y2="13" />
    <line x1="6" y1="8" x2="18" y2="8" /><line x1="6" y1="11" x2="18" y2="11" />
  </svg>
);

const BowlingPin = ({ className, ...props }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
    <path d="M12 2c-1.5 0-2.5 1-2.5 2.5 0 1 .5 1.8 1 2.5-.8 1-1.5 2.5-1.5 4 0 1 .3 2 .8 2.8-.5 1.2-.8 2.5-.8 3.7 0 2.5 1.3 4.5 3 4.5s3-2 3-4.5c0-1.2-.3-2.5-.8-3.7.5-.8.8-1.8.8-2.8 0-1.5-.7-3-1.5-4 .5-.7 1-1.5 1-2.5C14.5 3 13.5 2 12 2z" />
  </svg>
);

const sportIcons = {
  cycling: Bike, running: Footprints, triathlon: Medal, walking: PersonStanding,
  motorsport: Car, rallye: Gauge, vtt: Mountain, bmx: Zap, cyclocross: TreePine,
  racquet: TennisRacquet, archery: Target, kitesurf: Wind, golf: Flag,
  petanque: CircleDot, billard: Diamond, bowling: BowlingPin,
  crossfit: Dumbbell, combat: Swords, hyrox: Flame
};

const EventsPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState(searchParams.get('view') === 'map' ? 'map' : 'grid');
  const [mapCenter, setMapCenter] = useState(null);
  const [mapZoom, setMapZoom] = useState(null);

  // Read map params from URL on mount
  useEffect(() => {
    const lat = searchParams.get('lat');
    const lng = searchParams.get('lng');
    const zoom = searchParams.get('zoom');
    if (lat && lng) {
      setMapCenter([parseFloat(lat), parseFloat(lng)]);
      setMapZoom(zoom ? parseInt(zoom) : 8);
    }
  }, []);

  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    sport_type: searchParams.get('sport_type') || '',
    location: searchParams.get('location') || '',
    page: parseInt(searchParams.get('page')) || 1
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await categoriesApi.getAll();
        setCategories(res.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true);
      try {
        const params = { limit: 50 };
        if (filters.search) params.search = filters.search;
        if (filters.sport_type) params.sport_type = filters.sport_type;
        if (filters.location) params.location = filters.location;
        params.page = filters.page;

        const res = await eventsApi.getAll(params);
        setEvents(res.data.events);
        setTotalPages(res.data.pages);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();

    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.sport_type) params.set('sport_type', filters.sport_type);
    if (filters.location) params.set('location', filters.location);
    if (filters.page > 1) params.set('page', filters.page.toString());
    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ search: '', sport_type: '', location: '', page: 1 });
  };

  const activeFiltersCount = [filters.search, filters.sport_type, filters.location].filter(Boolean).length;

  // Group events by month
  const groupedEvents = events.reduce((acc, event) => {
    try {
      const date = new Date(event.date);
      const key = format(date, 'yyyy-MM');
      const label = format(date, 'MMMM yyyy', { locale: getDateLocale() });
      if (!acc[key]) acc[key] = { label, events: [] };
      acc[key].events.push(event);
    } catch {
      const key = 'unknown';
      if (!acc[key]) acc[key] = { label: t('events.dateUndefined'), events: [] };
      acc[key].events.push(event);
    }
    return acc;
  }, {});

  const sortedMonths = Object.keys(groupedEvents).sort();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="events-page">
      {/* Hero Search */}
      <div className="relative bg-asphalt overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand rounded-full translate-x-1/2 -translate-y-1/2 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand rounded-full -translate-x-1/2 translate-y-1/2 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-white">
              {t('events.allEvents')}
            </h1>
            <p className="text-slate-400 mt-3 max-w-xl">
              {t('events.discoverSubtitle')}
            </p>
          </motion.div>

          {/* Big Search Bar */}
          <motion.div
            className="mt-10 bg-white/10 backdrop-blur-xl border border-white/20 p-2 md:p-3"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          >
            <form onSubmit={(e) => { e.preventDefault(); }} className="flex flex-col md:flex-row gap-2 md:gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder={t('events.searchPlaceholder')}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full h-14 pl-12 pr-4 bg-white text-slate-900 placeholder:text-slate-400 font-medium text-base outline-none focus:ring-2 focus:ring-brand transition-all"
                  data-testid="search-input"
                />
              </div>
              <div className="relative w-full md:w-56">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5 z-10" />
                <input
                  type="text"
                  placeholder={t('events.cityOrRegion')}
                  value={filters.location}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full h-14 pl-12 pr-4 bg-white text-slate-900 placeholder:text-slate-400 font-medium text-base outline-none focus:ring-2 focus:ring-brand transition-all"
                  data-testid="location-input"
                />
              </div>
            </form>
          </motion.div>

          {/* Sport Category Pills */}
          <motion.div
            className="mt-5 flex flex-wrap gap-2"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.3 }}
          >
            <button
              onClick={() => handleFilterChange('sport_type', '')}
              className={`px-4 py-2 text-xs font-heading font-bold uppercase tracking-wider transition-all ${
                !filters.sport_type ? 'bg-brand text-white' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/20'
              }`}
              data-testid="filter-all"
            >
              {t('events.all')}
            </button>
            {categories.map(cat => {
              const Icon = sportIcons[cat.id] || Footprints;
              return (
                <button
                  key={cat.id}
                  onClick={() => handleFilterChange('sport_type', cat.id === filters.sport_type ? '' : cat.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 text-xs font-heading font-bold uppercase tracking-wider transition-all ${
                    filters.sport_type === cat.id ? 'bg-brand text-white' : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white border border-white/20'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t(`sports.${cat.id}`, cat.name)}
                </button>
              );
            })}
          </motion.div>
        </div>
      </div>

      {/* Active Filters & View Toggle */}
      <div className="sticky top-16 z-40 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              {activeFiltersCount > 0 ? (
                <>
                  <span className="text-sm text-slate-500 hidden sm:inline">{t('events.filters')} :</span>
                  {filters.search && (
                    <span className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 text-xs font-bold uppercase tracking-wider">
                      <Search className="w-3 h-3" />
                      {filters.search}
                      <button onClick={() => handleFilterChange('search', '')} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {filters.sport_type && filters.sport_type !== 'all' && (
                    <span className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 text-xs font-bold uppercase tracking-wider">
                      {t(`sports.${filters.sport_type}`, categories.find(c => c.id === filters.sport_type)?.name || filters.sport_type)}
                      <button onClick={() => handleFilterChange('sport_type', '')} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {filters.location && (
                    <span className="inline-flex items-center gap-1.5 bg-brand/10 text-brand px-3 py-1 text-xs font-bold uppercase tracking-wider">
                      <MapPin className="w-3 h-3" />
                      {filters.location}
                      <button onClick={() => handleFilterChange('location', '')} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  <button onClick={clearFilters} className="text-xs text-slate-500 hover:text-brand font-bold uppercase tracking-wider underline">
                    {t('events.clearAll')}
                  </button>
                </>
              ) : (
                <span className="text-sm text-slate-500">
                  {!loading && <>{events.length} {t('events.published')}</>}
                </span>
              )}
            </div>
            <div className="hidden md:flex items-center gap-1 border border-slate-200 p-1">
              <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-brand text-white' : 'hover:bg-slate-100'}`} data-testid="view-grid">
                <Grid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-brand text-white' : 'hover:bg-slate-100'}`} data-testid="view-list">
                <List className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('map')} className={`p-2 transition-colors ${viewMode === 'map' ? 'bg-brand text-white' : 'hover:bg-slate-100'}`} data-testid="view-map">
                <Map className="w-4 h-4" />
              </button>
            </div>
            {/* Mobile view toggle */}
            <div className="flex md:hidden items-center gap-1 border border-slate-200 p-1">
              <button onClick={() => setViewMode('grid')} className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-brand text-white' : 'hover:bg-slate-100'}`}>
                <Grid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode('map')} className={`p-2 transition-colors ${viewMode === 'map' ? 'bg-brand text-white' : 'hover:bg-slate-100'}`}>
                <Map className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Sponsor Strip Banner */}
      <SponsorStripBanner sportType={filters.sport_type} region={filters.location} />

      {/* Events content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="loader" />
          </div>
        ) : events.length > 0 ? (
          <>
            {viewMode === 'map' ? (
              <React.Suspense fallback={<div className="flex justify-center py-20"><div className="loader" /></div>}>
                <EventsMap events={events} initialCenter={mapCenter} initialZoom={mapZoom} />
              </React.Suspense>
            ) : (
          <div className="space-y-12">
            {sortedMonths.map((monthKey, mIdx) => {
              const group = groupedEvents[monthKey];
              return (
                <motion.section key={monthKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: mIdx * 0.1 }}
                  data-testid={`month-section-${monthKey}`}
                >
                  {/* Month header */}
                  <div className="flex items-center gap-4 mb-6">
                    <div className="bg-asphalt text-white px-4 py-2.5 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-brand" />
                      <span className="font-heading font-bold text-sm uppercase tracking-wider capitalize">{group.label}</span>
                    </div>
                    <div className="flex-1 h-px bg-slate-200" />
                    <span className="text-xs text-slate-400 font-heading uppercase tracking-wider">{group.events.length} {group.events.length > 1 ? t('map.events') : t('map.events')}</span>
                  </div>

                  {/* Events grid */}
                  <div className={viewMode === 'grid'
                    ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
                    : 'flex flex-col gap-4'
                  }>
                    {group.events.map((event, idx) => (
                      <motion.div
                        key={event.event_id}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25, delay: idx * 0.04 }}
                      >
                        <EventCard event={event} />
                      </motion.div>
                    ))}
                  </div>
                </motion.section>
              );
            })}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                <Button
                  variant="outline"
                  disabled={filters.page === 1}
                  onClick={() => handleFilterChange('page', filters.page - 1)}
                  data-testid="prev-page-btn"
                >
                  {t('events.previous')}
                </Button>
                <span className="flex items-center px-4 font-heading text-sm">
                  {t('events.pageOf', { current: filters.page, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  disabled={filters.page === totalPages}
                  onClick={() => handleFilterChange('page', filters.page + 1)}
                  data-testid="next-page-btn"
                >
                  {t('events.next')}
                </Button>
              </div>
            )}

            {/* Showcase vitrines - always visible */}
            <div className="mt-12 pt-12 border-t border-slate-200">
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="showcase-banner-events">
                <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  <strong>Apercu :</strong> Voici a quoi pourrait ressembler votre evenement sur iBookSport.
                  <Link to="/register?role=organizer" className="ml-1 text-brand font-bold hover:underline">
                    Creez le votre gratuitement
                  </Link>
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {showcaseEvents.map((event, idx) => (
                  <motion.div
                    key={event.event_id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: idx * 0.04 }}
                  >
                    <EventCard event={event} />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
            )}
          </>
        ) : (
          <div className="py-12">
            <div className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3" data-testid="showcase-banner-events">
              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                <strong>Apercu :</strong> Voici a quoi ressembleront les evenements sur iBookSport.
                <Link to="/register?role=organizer" className="ml-1 text-brand font-bold hover:underline">
                  Creez le votre gratuitement
                </Link>
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {showcaseEvents.map((event, idx) => (
                <motion.div
                  key={event.event_id}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: idx * 0.04 }}
                >
                  <EventCard event={event} />
                </motion.div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
